/**
 * Pure helpers for `UrlParseTool`. Tests live in
 * `tests/web/urlParseTool.test.ts`.
 *
 * Backed by the WHATWG `URL` parser native to evergreen browsers and
 * Node 18+. We deliberately do *not* fall back to a hand-rolled
 * tokenizer for invalid input — surfacing "Invalid URL" matches what
 * `fetch` will do and avoids encouraging users to ship URLs the spec
 * already rejects.
 */

export interface ParsedQueryItem {
  key: string;
  /** Raw `value` segment as it appeared in `?key=raw`. Includes any percent-encoding. */
  raw: string;
  /** Decoded form via `decodeURIComponent`; falls back to `raw` if decoding fails. */
  decoded: string;
}

export interface ParsedUrl {
  protocol: string;
  username: string;
  password: string;
  /** `host:port` if a port is present; otherwise `hostname`. */
  host: string;
  hostname: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
  /** One row per query parameter, preserving order and duplicates. */
  query: ParsedQueryItem[];
  isValid: boolean;
  /** Set when we silently prepended `https://` to recover from a bare host. */
  autoProtocolAdded?: boolean;
  /** Populated when `isValid` is `false`. */
  error?: string;
  /** Best-effort canonical URL after re-serialising. Useful for "拼回" copy. */
  href?: string;
}

const EMPTY: ParsedUrl = {
  protocol: '',
  username: '',
  password: '',
  host: '',
  hostname: '',
  port: '',
  pathname: '',
  search: '',
  hash: '',
  query: [],
  isValid: false,
};

/**
 * Parse `input` into a `ParsedUrl`. Always returns an object — never
 * throws — so callers can render `error` directly.
 */
export function parseUrl(input: string): ParsedUrl {
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    return { ...EMPTY, error: '输入为空' };
  }

  // Try as-is first; if that fails AND the string lacks a protocol,
  // retry with `https://` prepended. We don't auto-prepend
  // unconditionally because some valid inputs (e.g. `mailto:user@x`)
  // should be parsed verbatim.
  const direct = tryParse(trimmed);
  if (direct) return finalise(direct, false);

  if (!hasProtocol(trimmed)) {
    const withProto = tryParse(`https://${trimmed}`);
    if (withProto) return finalise(withProto, true);
  }

  return { ...EMPTY, error: 'Invalid URL' };
}

function finalise(u: URL, autoProtocolAdded: boolean): ParsedUrl {
  return {
    protocol: u.protocol,
    username: u.username,
    password: u.password,
    host: u.host,
    hostname: u.hostname,
    port: u.port,
    pathname: u.pathname,
    search: u.search,
    hash: u.hash,
    query: parseQuery(u.search),
    isValid: true,
    autoProtocolAdded: autoProtocolAdded || undefined,
    href: u.toString(),
  };
}

function tryParse(s: string): URL | null {
  try {
    return new URL(s);
  } catch {
    return null;
  }
}

function hasProtocol(s: string): boolean {
  // RFC 3986 scheme: ALPHA *( ALPHA / DIGIT / "+" / "-" / "." ) ":" .
  return /^[a-z][a-z0-9+\-.]*:/i.test(s);
}

/**
 * Parse `?a=1&b=2&a=3` → `[{key:'a', raw:'1'},{key:'b', raw:'2'},{key:'a', raw:'3'}]`.
 *
 * We can't use `URLSearchParams` here because it eagerly decodes the
 * raw value, which we want to preserve so users can spot encoding
 * bugs (e.g. double-encoded `%2520`).
 */
export function parseQuery(search: string): ParsedQueryItem[] {
  const body = search.startsWith('?') ? search.slice(1) : search;
  if (body.length === 0) return [];

  const out: ParsedQueryItem[] = [];
  for (const pair of body.split('&')) {
    if (pair.length === 0) continue;
    const eq = pair.indexOf('=');
    const rawKey = eq === -1 ? pair : pair.slice(0, eq);
    const rawValue = eq === -1 ? '' : pair.slice(eq + 1);
    out.push({
      key: safeDecode(rawKey),
      raw: rawValue,
      decoded: safeDecode(rawValue),
    });
  }
  return out;
}

function safeDecode(s: string): string {
  // `decodeURIComponent` throws on lone `%` characters that are
  // sometimes legal in real-world URLs (e.g. WeChat redirects). Fall
  // back to the raw input rather than crashing the whole row.
  try {
    return decodeURIComponent(s.replace(/\+/g, ' '));
  } catch {
    return s;
  }
}
